import { SPACES_CONFIG } from '../config/constants';

/**
 * Service for handling DigitalOcean Spaces direct requests
 */
class SpacesService {
  /**
   * Fetch GeoJSON data directly from DigitalOcean Spaces
   * @param {number} projectId - Project ID
   * @param {string} type - Type of GeoJSON (compressed, ultra, pretty)
   * @returns {Promise<Object>} GeoJSON data
   */
  async fetchGeoJsonDirect(projectId, type = 'compressed') {
    if (!SPACES_CONFIG.isDirectAccessEnabled()) {
      throw new Error('Direct Spaces access is not enabled');
    }

    const url = SPACES_CONFIG.getGeoJsonUrl(projectId, type);
    
    // console.log('####url', url);
    try {
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'public, max-age=1800', // 30 minutes
        },
        // Add timeout
        signal: AbortSignal.timeout(30000) // 30 seconds timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle gzipped content for ultra type
      let data;
      if (type === 'ultra' && response.headers.get('content-encoding') === 'gzip') {
        // For gzipped content, the browser should automatically decompress
        data = await response.json();
      } else {
        data = await response.json();
      }

      return {
        data,
        source: 'spaces',
        url,
        cached: response.headers.get('x-cache') === 'HIT'
      };

    } catch (error) {
      console.error(`❌ Failed to fetch GeoJSON from Spaces for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Check if a GeoJSON file exists in Spaces
   * @param {number} projectId - Project ID
   * @param {string} type - Type of GeoJSON
   * @returns {Promise<boolean>} Whether the file exists
   */
  async checkGeoJsonExists(projectId, type = 'compressed') {
    if (!SPACES_CONFIG.isDirectAccessEnabled()) {
      return false;
    }

    const url = SPACES_CONFIG.getGeoJsonUrl(projectId, type);
    
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        signal: AbortSignal.timeout(10000) // 10 seconds timeout
      });
      
      return response.ok;
    } catch (error) {
      console.warn(`Could not check if GeoJSON exists in Spaces for project ${projectId}:`, error);
      return false;
    }
  }

  /**
   * Get file information from Spaces
   * @param {number} projectId - Project ID
   * @param {string} type - Type of GeoJSON
   * @returns {Promise<Object>} File information
   */
  async getGeoJsonInfo(projectId, type = 'compressed') {
    if (!SPACES_CONFIG.isDirectAccessEnabled()) {
      throw new Error('Direct Spaces access is not enabled');
    }

    const url = SPACES_CONFIG.getGeoJsonUrl(projectId, type);
    
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        signal: AbortSignal.timeout(10000) // 10 seconds timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return {
        exists: true,
        size: response.headers.get('content-length'),
        lastModified: response.headers.get('last-modified'),
        etag: response.headers.get('etag'),
        contentType: response.headers.get('content-type'),
        cacheControl: response.headers.get('cache-control'),
        url
      };

    } catch (error) {
      console.error(`Failed to get GeoJSON info from Spaces for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Prefetch GeoJSON data for multiple projects
   * @param {Array<number>} projectIds - Array of project IDs
   * @param {string} type - Type of GeoJSON
   * @returns {Promise<Object>} Map of project ID to GeoJSON data
   */
  async prefetchGeoJsonBatch(projectIds, type = 'compressed') {
    if (!SPACES_CONFIG.isDirectAccessEnabled()) {
      throw new Error('Direct Spaces access is not enabled');
    }

    const results = {};
    const promises = projectIds.map(async (projectId) => {
      try {
        const result = await this.fetchGeoJsonDirect(projectId, type);
        results[projectId] = result;
      } catch (error) {
        console.warn(`Failed to prefetch GeoJSON for project ${projectId}:`, error);
        results[projectId] = { error: error.message };
      }
    });

    await Promise.allSettled(promises);
    return results;
  }

  /**
   * Get the direct URL for a GeoJSON file
   * @param {number} projectId - Project ID
   * @param {string} type - Type of GeoJSON
   * @returns {string} Direct URL
   */
  getDirectUrl(projectId, type = 'compressed') {
    return SPACES_CONFIG.getGeoJsonUrl(projectId, type);
  }

  /**
   * Check if direct access is available
   * @returns {boolean} Whether direct access is enabled
   */
  isDirectAccessAvailable() {
    return SPACES_CONFIG.isDirectAccessEnabled();
  }

  /**
   * Get Spaces configuration
   * @returns {Object} Spaces configuration
   */
  getConfig() {
    return {
      originUrl: SPACES_CONFIG.ORIGIN_URL,
      cdnUrl: SPACES_CONFIG.CDN_URL,
      directAccess: SPACES_CONFIG.DIRECT_GEOJSON_ACCESS,
      patterns: SPACES_CONFIG.GEOJSON_PATTERNS
    };
  }
}

// Create and export a singleton instance
const spacesService = new SpacesService();
export default spacesService;
